import React, { useState, useEffect } from 'react';
import { Lock, User, Mail, Phone, KeyRound, X, Loader2 } from 'lucide-react';
import { updateAdminPassword } from '../../api/Adminloginapi';
import { toast } from '../shared/Toast';
import { getUser, saveUser } from '../../utils/auth';

const ProfileSettings = () => {
  const [formData, setFormData] = useState({
    fullName: 'SST Admin',
    email: '',
    phone: '+91 98765 43210'
  });

  useEffect(() => {
    const user = getUser();
    let email = sessionStorage.getItem('userEmail') || user?.email || localStorage.getItem('userEmail') || '';

    // If still not found, extract from JWT token if available
    if (!email) {
      try {
        const token = sessionStorage.getItem('token');
        if (token) {
          const parts = token.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1]));
            email = payload.email || '';
          }
        }
      } catch (e) {
        console.error('Error extracting email from token:', e);
      }
    }

    const fullName = user?.name || 'SST Admin';
    const phone = user?.phone || '+91 98765 43210';

    setFormData(prev => ({
      ...prev,
      email: email || prev.email,
      fullName: fullName || prev.fullName,
      phone: phone || prev.phone
    }));

    if (email && !sessionStorage.getItem('userEmail')) {
      sessionStorage.setItem('userEmail', email);
    }
  }, []);

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdate = () => {
    const currentUser = getUser() || {};
    const updatedUser = {
      ...currentUser,
      name: formData.fullName,
      email: formData.email,
      phone: formData.phone
    };
    saveUser(updatedUser);
    sessionStorage.setItem('userEmail', formData.email);
    toast.success('Profile updated successfully!');
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage('Passwords do not match!');
      return;
    }
    setPasswordLoading(true);
    setPasswordMessage('');
    try {
      const data = await updateAdminPassword(passwordData.newPassword);
      if (data.success) {
        setPasswordMessage('Password updated successfully!');
        setTimeout(() => {
          setPasswordData({ newPassword: '', confirmPassword: '' });
          setShowPassword(false);
          setPasswordMessage('');
        }, 1500);
      } else {
        setPasswordMessage(data.message || 'Failed to update password');
      }
    } catch (err) {
      setPasswordMessage(err.message || 'Password update failed. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowPassword(false);
    setPasswordData({ newPassword: '', confirmPassword: '' });
    setPasswordMessage('');
    setPasswordLoading(false);
  };

  const pageStyle = {
    minHeight: '100vh',
    backgroundColor: '#F3F4F6',
    padding: '0 24px 24px 24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start'
  };

  const cardStyle = {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden'
  };

  const cardHeaderStyle = {
    backgroundColor: '#07A95B',
    padding: '32px',
    color: '#FFFFFF',
    textAlign: 'center'
  };

  const avatarStyle = {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px auto',
    border: '3px solid rgba(255,255,255,0.3)'
  };

  const cardBodyStyle = {
    padding: '32px'
  };

  const sectionStyle = {
    marginBottom: '32px'
  };

  const sectionTitleStyle = {
    fontSize: '12px',
    fontWeight: 700,
    color: '#07A95B',
    margin: '0 0 20px 0',
    paddingBottom: '10px',
    borderBottom: '2px solid #E5E7EB',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  };

  const formGroupStyle = {
    marginBottom: '20px'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 600,
    color: '#4B5563',
    marginBottom: '8px'
  };

  const inputWrapperStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  };

  const inputIconStyle = {
    position: 'absolute',
    left: '14px',
    color: '#9CA3AF',
    pointerEvents: 'none'
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 16px 12px 44px',
    borderRadius: '10px',
    border: '1.5px solid #E5E7EB',
    fontSize: '14px',
    outline: 'none',
    backgroundColor: '#F9FAFB',
    color: '#111827',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box'
  };

  const updateButtonStyle = {
    backgroundColor: '#07A95B',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '10px',
    padding: '12px 28px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(7,169,91,0.3)'
  };

  const secondaryButtonStyle = {
    backgroundColor: '#6B7280',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '10px',
    padding: '12px 28px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease'
  };

  const linkButtonStyle = {
    background: '#ECFDF5',
    border: '1.5px solid #07A95B',
    color: '#07A95B',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    padding: '10px 20px',
    borderRadius: '10px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease'
  };

  const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '24px'
  };

  const modalStyle = {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
    width: '100%',
    maxWidth: '420px',
    overflow: 'hidden'
  };

  const modalHeaderStyle = {
    backgroundColor: '#07A95B',
    padding: '24px',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const modalBodyStyle = {
    padding: '28px'
  };

  const modalFooterStyle = {
    padding: '0 28px 28px 28px',
    display: 'flex',
    gap: '12px'
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        {/* Card Header */}
        <div style={cardHeaderStyle}>
          <div style={avatarStyle}>
            <User size={32} color="#FFFFFF" />
          </div>
          <h2 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: 700 }}>
            {formData.fullName}
          </h2>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
            {formData.email}
          </p>
        </div>

        <div style={cardBodyStyle}>
          {/* Profile Form */}
          <div style={sectionStyle}>
            <h3 style={sectionTitleStyle}>Profile Details</h3>

            <form onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>Full Name</label>
                <div style={inputWrapperStyle}>
                  <span style={inputIconStyle}><User size={18} /></span>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => handleChange('fullName', e.target.value)}
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = '#07A95B'}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  />
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Email Address</label>
                <div style={inputWrapperStyle}>
                  <span style={inputIconStyle}><Mail size={18} /></span>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = '#07A95B'}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  />
                </div>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Phone Number</label>
                <div style={inputWrapperStyle}>
                  <span style={inputIconStyle}><Phone size={18} /></span>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = '#07A95B'}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  />
                </div>
              </div>

              <button
                type="submit"
                style={updateButtonStyle}
                onMouseEnter={(e) => { e.target.style.backgroundColor = '#05914D'; e.target.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.target.style.backgroundColor = '#07A95B'; e.target.style.transform = 'translateY(0)'; }}
              >
                <Lock size={16} />
                Update Profile
              </button>
            </form>
          </div>

          {/* Password Section */}
          <div>
            <h3 style={sectionTitleStyle}>Change Password</h3>

            <button
              onClick={() => setShowPassword(true)}
              style={linkButtonStyle}
              onMouseEnter={(e) => { e.target.style.backgroundColor = '#07A95B'; e.target.style.color = '#FFFFFF'; }}
              onMouseLeave={(e) => { e.target.style.backgroundColor = '#ECFDF5'; e.target.style.color = '#07A95B'; }}
            >
              <KeyRound size={16} />
              Change Password
            </button>
          </div>
        </div>

        {/* Change Password Modal */}
        {showPassword && (
          <div style={modalOverlayStyle} onClick={handleCloseModal}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
              <div style={modalHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <KeyRound size={22} />
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Change Password</h3>
                </div>
                <button
                  onClick={handleCloseModal}
                  style={{ background: 'none', border: 'none', color: '#FFFFFF', cursor: 'pointer', padding: '4px' }}
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleChangePassword(); }}>
                <div style={modalBodyStyle}>
                  {passwordMessage && (
                    <div
                      style={{
                        padding: '12px 16px',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 500,
                        marginBottom: '16px',
                        backgroundColor: passwordMessage.includes('success') ? '#ECFDF5' : '#FEF2F2',
                        color: passwordMessage.includes('success') ? '#05914D' : '#EF4444',
                        border: `1px solid ${passwordMessage.includes('success') ? '#A7F3D0' : '#FECACA'}`
                      }}
                    >
                      {passwordMessage}
                    </div>
                  )}
                  <div style={formGroupStyle}>
                    <label style={labelStyle}>New Password</label>
                    <div style={inputWrapperStyle}>
                      <span style={inputIconStyle}><Lock size={18} /></span>
                      <input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                        style={inputStyle}
                        placeholder="Enter new password"
                        onFocus={(e) => e.target.style.borderColor = '#07A95B'}
                        onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                      />
                    </div>
                  </div>

                  <div style={{ ...formGroupStyle, marginBottom: 0 }}>
                    <label style={labelStyle}>Confirm Password</label>
                    <div style={inputWrapperStyle}>
                      <span style={inputIconStyle}><Lock size={18} /></span>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                        style={inputStyle}
                        placeholder="Confirm new password"
                        onFocus={(e) => e.target.style.borderColor = '#07A95B'}
                        onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                      />
                    </div>
                  </div>
                </div>

                <div style={modalFooterStyle}>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    style={secondaryButtonStyle}
                    onMouseEnter={(e) => { e.target.style.backgroundColor = '#4B5563'; e.target.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = '#6B7280'; e.target.style.transform = 'translateY(0)'; }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    style={{ ...updateButtonStyle, flex: 1, justifyContent: 'center', opacity: passwordLoading ? 0.7 : 1, cursor: passwordLoading ? 'not-allowed' : 'pointer' }}
                    onMouseEnter={(e) => { if (!passwordLoading) { e.target.style.backgroundColor = '#05914D'; e.target.style.transform = 'translateY(-1px)'; }}}
                    onMouseLeave={(e) => { if (!passwordLoading) { e.target.style.backgroundColor = '#07A95B'; e.target.style.transform = 'translateY(0)'; }}}
                  >
                    {passwordLoading ? (
                      <>
                        <Loader2 size={16} className="spinner" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <Lock size={16} />
                        Update Password
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileSettings;
